/**
 * Mapa do trajeto de um veículo: por onde o carro provavelmente andou, onde ficou, onde está.
 *
 * ## O que é fato e o que é cálculo
 *
 * O aparelho não grava um caminho — ele grava PONTOS, de tempos em tempos. Tudo o que existe entre
 * dois pontos é reconstrução, e o mapa tem de deixar isso à vista, porque a conclusão que se tira
 * de «passou na frente da casa de Fulano» depende inteiramente de saber se aquilo foi medido ou
 * inferido. Então há duas camadas com pesos diferentes:
 *
 * - **as bolinhas** são o fato: cada uma é uma posição que o aparelho reportou, com hora e
 *   velocidade. É o único dado duro que existe;
 * - **a linha** é a rota provável, calculada pelas ruas (Directions) entre um reporte e o
 *   seguinte. Ela segue a malha viária em vez de cortar quarteirão, parque e lago — mas continua
 *   sendo o caminho que um motorista FARIA, não o que este motorista fez.
 *
 * E dentro da inferência ainda há dois graus, porque não são a mesma afirmação. Entre dois
 * reportes separados por segundos, a rota pelas ruas é quase certa: não há muito por onde
 * divergir. Entre dois reportes separados por dez minutos cabem desvios, paradas e caminhos
 * inteiramente diferentes — esse trecho vai **tracejado**, e clicá-lo diz quanto tempo ninguém
 * registrou. Um palpite forte e um palpite fraco desenhados igual seriam a mesma mentira de antes,
 * só que mais bonita.
 *
 * Quando o Directions não responde (chave sem a API ligada, cota, trecho sem rota terrestre), o
 * trecho cai para a reta entre os dois pontos e o componente AVISA quantos trechos ficaram assim —
 * o mapa nunca fica vazio, e nunca finge que uma reta é uma rota.
 *
 * ## Marcadores
 *
 * Tudo aqui é `AdvancedMarkerElement`: `google.maps.Marker` está depreciado desde 2024 e o produto
 * não carrega API depreciada. Marcador moderno exige Map ID no mapa — ver `lib/mapas`.
 */
import { useEffect, useRef, useState } from 'react';
import { garantirOpcoes, importLibrary, MAP_ID } from '@/lib/mapas';

export type PontoTrajeto = { em: string; lat: number; lon: number; kmh: number | null };
export type ParadaMapa = {
  id: string;
  inicio: string;
  duracao_seg: number;
  lat: number;
  lon: number;
  endereco: string | null;
};

/** O que o mapa aprendeu ao desenhar — a tela usa para contar o que não deu para calcular. */
export type EstadoRota = {
  /** Trechos desenhados como rota de rua. */
  calculados: number;
  /** Trechos que caíram para a reta porque o cálculo não veio. */
  retas: number;
  /** O serviço de rotas recusou (API não habilitada, cota estourada): parar de tentar. */
  indisponivel: boolean;
};

/**
 * A partir de quanto silêncio entre dois reportes o trecho deixa de ser palpite forte.
 *
 * O aparelho é configurado para reportar a cada 20s. Três minutos são nove marcas perdidas — a
 * 60 km/h, três quilômetros em que o carro pode ter feito qualquer coisa.
 */
const LIMIAR_GAP_SEG = 180;
/** Abaixo disto o carro praticamente não saiu do lugar: pedir rota seria gastar por nada. */
const DIST_MIN_ROTA_M = 25;
/** Teto do Directions: origem + 23 intermediários + destino. */
const MAX_PONTOS_POR_PEDIDO = 25;
/** Teto de pedidos por desenho. O Directions é cobrado por chamada; um trajeto de mês inteiro não
 * pode virar uma fatura surpresa porque alguém abriu a janela «Tudo». */
const MAX_PEDIDOS_POR_DESENHO = 12;
/**
 * Teto de trechos que ganham rota de rua — os MAIS RECENTES, que é o que se está investigando.
 *
 * O teto de pedidos sozinho não seguraria a conta: cada desenho pegaria o próximo lote e a janela
 * «Tudo» de um aparelho de meses acabaria roteada inteira, um lote por vez. Aqui o corte é do
 * total: o que passar do limite fica em reta, e a legenda conta quantos foram. Um limite que se
 * contorna sozinho depois de algumas voltas não é limite.
 */
const MAX_TRECHOS_ROTEADOS = MAX_PEDIDOS_POR_DESENHO * (MAX_PONTOS_POR_PEDIDO - 1);
/** Acima disto as bolinhas viram mancha e o navegador sofre: a linha já conta a história. */
const MAX_BOLINHAS = 500;

/**
 * As rotas já calculadas, por par de pontos, vivas enquanto a aba viver.
 *
 * Fica FORA do componente de propósito: o mapa redesenha a cada quinze segundos com a chegada de
 * posição nova, e recalcular os mesmos trechos a cada tique seria pagar de novo, para sempre, pela
 * mesma resposta. `null` no lugar do caminho significa «já perguntei e não há rota» — memória do
 * fracasso, para não insistir.
 */
const rotasEmCache = new Map<string, google.maps.LatLngLiteral[] | null>();
/** Ligado quando o serviço recusa por configuração ou cota: aí não é o trecho, é o serviço. */
let direcoesIndisponiveis = false;

function chaveDoTrecho(a: PontoTrajeto, b: PontoTrajeto): string {
  return `${a.lat.toFixed(5)},${a.lon.toFixed(5)}>${b.lat.toFixed(5)},${b.lon.toFixed(5)}`;
}

function duracaoHumana(seg: number): string {
  const h = Math.floor(seg / 3600);
  const m = Math.round((seg % 3600) / 60);
  if (h) return `${h}h${String(m).padStart(2, '0')}`;
  return m >= 1 ? `${m} min` : `${Math.round(seg)}s`;
}

/** Distância em linha reta, em metros. A mesma haversine do servidor. */
function distanciaM(a: PontoTrajeto, b: PontoTrajeto): number {
  const r = 6_371_000;
  const rad = (g: number) => (g * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const s =
    Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(s)));
}

function distanciaHumana(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}

const comoLatLng = (p: PontoTrajeto): google.maps.LatLngLiteral => ({ lat: p.lat, lng: p.lon });

type Trecho = {
  de: PontoTrajeto;
  para: PontoTrajeto;
  chave: string;
  gapSeg: number;
  metros: number;
  /** Silêncio longo o bastante para a rota ser palpite fraco. */
  incerto: boolean;
};

function emTrechos(pontos: PontoTrajeto[]): Trecho[] {
  const trechos: Trecho[] = [];
  for (let i = 1; i < pontos.length; i++) {
    const de = pontos[i - 1];
    const para = pontos[i];
    const gapSeg = (new Date(para.em).getTime() - new Date(de.em).getTime()) / 1000;
    trechos.push({
      de,
      para,
      chave: chaveDoTrecho(de, para),
      gapSeg,
      metros: distanciaM(de, para),
      incerto: gapSeg > LIMIAR_GAP_SEG,
    });
  }
  return trechos;
}

/**
 * Calcula, pelas ruas, os trechos que ainda não estão em cache. Devolve quantos aprendeu.
 *
 * Os pontos vão numa requisição só sempre que possível (origem + intermediários + destino), e a
 * resposta vem em `legs` — uma perna por par consecutivo. É o que permite pagar uma chamada e
 * receber vinte e quatro trechos, cada um guardado sob a sua própria chave.
 */
async function calcularRotas(trechos: Trecho[]): Promise<number> {
  if (direcoesIndisponiveis) return 0;
  const faltando = trechos
    .slice(-MAX_TRECHOS_ROTEADOS)
    .filter((t) => !rotasEmCache.has(t.chave) && t.metros >= DIST_MIN_ROTA_M);
  if (!faltando.length) return 0;

  const { DirectionsService } = await importLibrary('routes');
  const servico = new DirectionsService();
  let aprendidos = 0;

  // Fatia em corridas de trechos CONSECUTIVOS: só assim os pontos formam um caminho único que
  // cabe numa requisição. Um trecho isolado no meio da lista vira uma requisição de dois pontos.
  const lotes: Trecho[][] = [];
  for (const t of faltando) {
    const ultimo = lotes.at(-1);
    const emSequencia = ultimo && ultimo.at(-1)?.para === t.de;
    if (emSequencia && ultimo.length < MAX_PONTOS_POR_PEDIDO - 1) ultimo.push(t);
    else lotes.push([t]);
  }

  for (const lote of lotes.slice(0, MAX_PEDIDOS_POR_DESENHO)) {
    const pontos = [lote[0].de, ...lote.map((t) => t.para)];
    try {
      const r = await servico.route({
        origin: comoLatLng(pontos[0]),
        destination: comoLatLng(pontos[pontos.length - 1]),
        waypoints: pontos.slice(1, -1).map((p) => ({ location: comoLatLng(p), stopover: true })),
        travelMode: google.maps.TravelMode.DRIVING,
      });
      const pernas = r.routes[0]?.legs ?? [];
      lote.forEach((t, i) => {
        const perna = pernas[i];
        if (!perna) return;
        const caminho = perna.steps.flatMap((s) => s.path.map((c) => ({ lat: c.lat(), lng: c.lng() })));
        rotasEmCache.set(t.chave, caminho.length ? caminho : null);
        aprendidos += 1;
      });
    } catch (e) {
      const status = (e as { code?: string })?.code ?? '';
      if (status === 'ZERO_RESULTS' || status === 'NOT_FOUND') {
        // O trecho não tem rota de carro (travessia de balsa, ponto no meio do lago). É resposta,
        // não falha: guarda o «não há» para não perguntar de novo.
        for (const t of lote) rotasEmCache.set(t.chave, null);
        aprendidos += lote.length;
        continue;
      }
      // Cota, chave sem a API ligada, rede: o problema é o serviço, não este trecho. Parar de
      // tentar é o que separa um aviso na tela de uma enxurrada de chamadas cobradas que falham.
      direcoesIndisponiveis = true;
      break;
    }
  }
  return aprendidos;
}

export function MapaTrajeto({
  apiKey,
  pontos,
  paradas,
  onFalhar,
  onEstadoRota,
  chaveEnquadramento = 'inicial',
}: {
  apiKey: string;
  pontos: PontoTrajeto[];
  paradas: ParadaMapa[];
  onFalhar: () => void;
  /** Recebe o balanço do desenho — quantos trechos são rota e quantos ficaram em reta. */
  onEstadoRota?: (e: EstadoRota) => void;
  /** Muda quando o operador pede outro recorte (outra janela). Só então o zoom é refeito — a
   * chegada de posição nova NUNCA mexe no enquadramento de quem está olhando. */
  chaveEnquadramento?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mapa = useRef<google.maps.Map | null>(null);
  const info = useRef<google.maps.InfoWindow | null>(null);
  const marcadores = useRef<typeof google.maps.marker | null>(null);
  /** Como desfazer cada coisa desenhada na última passada. Polilinha se apaga com `setMap(null)`;
   * marcador moderno, com `map = null` — guardar a AÇÃO evita um `if` de tipo a cada limpeza. */
  const apagar = useRef<(() => void)[]>([]);
  const enquadrado = useRef<string | null>(null);
  const avisar = useRef(onEstadoRota);
  avisar.current = onEstadoRota;
  const [pronto, setPronto] = useState(false);
  /** Sobe quando novas rotas entram no cache, para o desenho se refazer com elas. */
  const [versaoRotas, setVersaoRotas] = useState(0);

  // O mapa nasce UMA vez. Recriá-lo a cada dado novo jogaria fora zoom, arraste e a janela de
  // informação aberta — e com o mapa vivo isso seria a cada quinze segundos.
  useEffect(() => {
    let cancelado = false;
    garantirOpcoes(apiKey);

    async function montar() {
      const [{ Map }, marcador] = await Promise.all([
        importLibrary('maps'),
        importLibrary('marker'),
      ]);
      if (cancelado || !ref.current) return;
      mapa.current = new Map(ref.current, {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        // Sem Map ID o `AdvancedMarkerElement` não renderiza: o mapa apareceria e as bolinhas não.
        mapId: MAP_ID,
        // Sem centro/zoom definitivos: o `fitBounds` do desenho decide a partir do dado. Estes
        // valores só existem porque a API exige algo para construir.
        center: { lat: -30.03, lng: -51.23 },
        zoom: 12,
      });
      info.current = new google.maps.InfoWindow();
      marcadores.current = marcador;
      setPronto(true);
    }

    montar().catch(() => {
      if (!cancelado) onFalhar();
    });

    return () => {
      cancelado = true;
    };
  }, [apiKey, onFalhar]);

  // O desenho, refeito a cada dado novo sobre o MESMO mapa.
  useEffect(() => {
    const m = mapa.current;
    const lib = marcadores.current;
    if (!pronto || !m || !lib) return;
    let cancelado = false;

    for (const desfazer of apagar.current) desfazer();
    apagar.current = [];

    const guardarLinha = (linha: google.maps.Polyline) => {
      apagar.current.push(() => linha.setMap(null));
      return linha;
    };
    const guardarMarcador = (marcador: google.maps.marker.AdvancedMarkerElement) => {
      apagar.current.push(() => {
        marcador.map = null;
      });
      return marcador;
    };

    const trechos = emTrechos(pontos);
    let calculados = 0;
    let retas = 0;

    // ── A rota. Trechos certos consecutivos viram UMA polilinha (mil polilinhas de dois pontos
    // travam o navegador); cada trecho incerto é a sua, porque cada um tem a sua história a
    // contar quando clicado.
    let acumulado: google.maps.LatLngLiteral[] = [];
    const despejarAcumulado = () => {
      if (acumulado.length < 2) {
        acumulado = [];
        return;
      }
      guardarLinha(
        new google.maps.Polyline({
          path: acumulado,
          map: m,
          strokeColor: '#f59e0b', // âmbar da identidade — a rota provável
          strokeOpacity: 0.85,
          strokeWeight: 4,
          zIndex: 2,
        }),
      );
      acumulado = [];
    };

    for (const t of trechos) {
      const cacheado = rotasEmCache.get(t.chave);
      const caminho = cacheado ?? [comoLatLng(t.de), comoLatLng(t.para)];
      if (cacheado) calculados += 1;
      else if (t.metros >= DIST_MIN_ROTA_M) retas += 1;

      if (!t.incerto) {
        // Emenda sem repetir o ponto de junção, que já é o fim do trecho anterior.
        acumulado = acumulado.length ? [...acumulado, ...caminho.slice(1)] : [...caminho];
        continue;
      }

      despejarAcumulado();
      const vao = guardarLinha(
        new google.maps.Polyline({
          path: caminho,
          map: m,
          strokeColor: '#94a3b8',
          strokeOpacity: 0, // opacidade zero é o que transforma a linha nos tracinhos abaixo
          icons: [
            {
              icon: {
                path: 'M 0,-1 0,1',
                strokeOpacity: 0.9,
                strokeColor: '#94a3b8',
                strokeWeight: 3,
                scale: 3,
              },
              offset: '0',
              repeat: '14px',
            },
          ],
          zIndex: 1,
        }),
      );
      vao.addListener('click', (e: google.maps.MapMouseEvent) => {
        info.current?.setContent(
          `<div style="font:13px/1.5 system-ui;max-width:250px">` +
            `<b>Sem posição por ${duracaoHumana(t.gapSeg)}</b><br>` +
            `${distanciaHumana(t.metros)} em linha reta entre uma marca e a seguinte.<br>` +
            `<span style="color:#64748b">${
              cacheado
                ? 'O caminho desenhado é o que um motorista faria pelas ruas — ninguém registrou ' +
                  'qual foi o real.'
                : 'Não foi possível calcular a rota deste trecho: a linha é a ligação reta entre ' +
                  'os dois pontos.'
            }</span></div>`,
        );
        info.current?.setPosition(e.latLng);
        info.current?.open({ map: m });
      });
    }
    despejarAcumulado();

    // ── As bolinhas: o único dado duro do mapa. Uma por posição reportada.
    const desenharBolinhas = pontos.length <= MAX_BOLINHAS;
    if (desenharBolinhas) {
      pontos.forEach((p, i) => {
        const ultimo = i === pontos.length - 1;
        const corpo = document.createElement('div');
        corpo.style.cssText = ultimo
          ? 'width:16px;height:16px;border-radius:50%;background:#f59e0b;border:3px solid #fff;' +
            'box-shadow:0 0 0 1px rgba(0,0,0,.25)'
          : 'width:9px;height:9px;border-radius:50%;background:#fff;border:3px solid #f59e0b';
        const marcador = guardarMarcador(
          new lib.AdvancedMarkerElement({
            map: m,
            position: comoLatLng(p),
            // O padrão do marcador é ancorar pela BASE, como um alfinete. A bolinha é o ponto em
            // si: tem de ficar centrada na coordenada, senão marca alguns metros ao norte.
            anchorTop: '-50%',
            anchorLeft: '-50%',
            zIndex: ultimo ? 999 : 3,
            title: `${new Date(p.em).toLocaleString('pt-BR')}${
              p.kmh != null ? ` · ${Math.round(p.kmh)} km/h` : ''
            }${ultimo ? ' · última posição recebida' : ''}`,
          }),
        );
        marcador.append(corpo);
      });
    }

    // ── As paradas, na ordem CRONOLÓGICA: a lista vem do mais recente, e um mapa numerado ao
    // contrário do relógio se lê errado.
    const emOrdem = [...paradas].sort((a, b) => a.inicio.localeCompare(b.inicio));
    emOrdem.forEach((p, i) => {
      const pino = new lib.PinElement({
        glyphText: String(i + 1),
        background: '#f59e0b',
        borderColor: '#ffffff',
        glyphColor: '#ffffff',
      });
      const marcador = guardarMarcador(
        new lib.AdvancedMarkerElement({
          map: m,
          position: { lat: p.lat, lng: p.lon },
          gmpClickable: true,
          zIndex: 500,
          title: `${duracaoHumana(p.duracao_seg)} — ${new Date(p.inicio).toLocaleString('pt-BR')}`,
        }),
      );
      marcador.append(pino);
      marcador.addEventListener('gmp-click', () => {
        const quando = new Date(p.inicio).toLocaleString('pt-BR');
        info.current?.setContent(
          `<div style="font:13px/1.5 system-ui"><b>Parada ${i + 1} · ${duracaoHumana(
            p.duracao_seg,
          )}</b><br>${quando}${p.endereco ? `<br>${p.endereco}` : ''}</div>`,
        );
        info.current?.open({ anchor: marcador, map: m });
      });
    });

    if (enquadrado.current !== chaveEnquadramento && (pontos.length || paradas.length)) {
      const limites = new google.maps.LatLngBounds();
      for (const p of pontos) limites.extend(comoLatLng(p));
      for (const p of emOrdem) limites.extend({ lat: p.lat, lng: p.lon });
      m.fitBounds(limites, 48);
      enquadrado.current = chaveEnquadramento;
    }

    avisar.current?.({ calculados, retas, indisponivel: direcoesIndisponiveis });

    // Só depois de a tela já estar desenhada com o que se sabe é que se vai buscar o que falta —
    // e um desenho novo acontece se, e só se, algo novo foi aprendido. Sem esse "se", cada volta
    // agendaria a seguinte e o efeito nunca pararia.
    void calcularRotas(trechos).then((aprendidos) => {
      if (!cancelado && aprendidos > 0) setVersaoRotas((v) => v + 1);
    });

    return () => {
      cancelado = true;
    };
  }, [pronto, pontos, paradas, chaveEnquadramento, versaoRotas]);

  return <div ref={ref} className="size-full" />;
}
